import React, { useState, useMemo, useEffect } from 'react';
import { SurveyTemplate } from '../../types/survey.types';
import {
  extractTemplateVariables,
  getVariableLocations,
  substituteVariables,
  variableNameToLabel,
} from '../../utils/templateVariables';
import { stripHTML } from '../../utils/stripHTML';

interface CreateFromTemplateModalProps {
  template: SurveyTemplate;
  onCreate: (title: string, description: string, variables?: Record<string, string>) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

const CreateFromTemplateModal: React.FC<CreateFromTemplateModalProps> = ({
  template,
  onClose,
  onCreate,
  loading,
}) => {
  // Detect variables from template content
  const detectedVarNames = useMemo(
    () => extractTemplateVariables(template),
    [template]
  );

  const variableLocations = useMemo(
    () => getVariableLocations(template),
    [template]
  );

  // Build variable definitions: merge auto-detected with any explicit metadata
  const variableDefs = useMemo(() => {
    const explicitMap = new Map(
      (template.variables || []).map(v => [v.name, v])
    );
    return detectedVarNames.map(name => ({
      name,
      displayLabel: explicitMap.get(name)?.displayLabel || variableNameToLabel(name),
      defaultValue: explicitMap.get(name)?.defaultValue || '',
      description: explicitMap.get(name)?.description || '',
      locations: variableLocations[name] || [],
    }));
  }, [detectedVarNames, template.variables, variableLocations]);

  const hasVariables = variableDefs.length > 0;

  // State for variable values
  const [variableValues, setVariableValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    variableDefs.forEach(v => {
      initial[v.name] = v.defaultValue;
    });
    return initial;
  });

  // Resolve template name with current variable values
  const resolvedTemplateName = useMemo(() => {
    if (!hasVariables) return template.name;
    return substituteVariables(template.name, variableValues);
  }, [template.name, variableValues, hasVariables]);

  const [title, setTitle] = useState(template.name);
  const [description, setDescription] = useState(stripHTML(template.description || ''));
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);

  // Auto-update title when variables change (unless user manually edited it)
  useEffect(() => {
    if (!titleManuallyEdited) {
      setTitle(resolvedTemplateName);
    }
  }, [resolvedTemplateName, titleManuallyEdited]);

  const updateVariable = (name: string, value: string) => {
    setVariableValues(prev => ({ ...prev, [name]: value }));
  };

  const allVariablesFilled = variableDefs.every(
    v => variableValues[v.name]?.trim().length > 0
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (hasVariables && !allVariablesFilled) return;

    await onCreate(
      title.trim(),
      description.trim(),
      hasVariables ? variableValues : undefined
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
            Create Survey from Template
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
          Creating from <strong>{template.name}</strong> ({template.questionCount} questions, {template.sectionCount} sections)
        </p>

        {/* Template Variables Section */}
        {hasVariables && (
          <div
            className="mb-5 p-4 rounded-xl border"
            style={{
              borderColor: 'var(--color-accent)',
              backgroundColor: 'var(--primary-50)',
            }}
          >
            <div className="flex items-center gap-2 mb-3">
              <svg className="w-4 h-4" style={{ color: 'var(--color-accent)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <h4 className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                Template Variables
              </h4>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{
                backgroundColor: 'var(--color-accent)',
                color: 'white',
              }}>
                {variableDefs.length}
              </span>
            </div>
            <p className="text-xs mb-3" style={{ color: 'var(--color-muted)' }}>
              Fill in these values to customise the survey content.
            </p>

            <div className="space-y-3">
              {variableDefs.map(varDef => (
                <div key={varDef.name}>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium" style={{ color: 'var(--color-primary)' }}>
                      {varDef.displayLabel} *
                    </label>
                    <span
                      className="text-xs cursor-help"
                      style={{ color: 'var(--color-muted)' }}
                      title={varDef.locations.join('\n')}
                    >
                      Used in {varDef.locations.length} {varDef.locations.length === 1 ? 'place' : 'places'}
                    </span>
                  </div>
                  {varDef.description && (
                    <p className="text-xs mb-1" style={{ color: 'var(--color-muted)' }}>
                      {varDef.description}
                    </p>
                  )}
                  <input
                    type="text"
                    value={variableValues[varDef.name] || ''}
                    onChange={(e) => updateVariable(varDef.name, e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white"
                    style={{ borderColor: 'var(--glass-border-light)' }}
                    placeholder={`Enter ${varDef.displayLabel.toLowerCase()}`}
                    autoFocus={variableDefs.indexOf(varDef) === 0}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Survey Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleManuallyEdited(true);
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: 'var(--glass-border-light)' }}
              placeholder="Enter survey title"
              required
              autoFocus={!hasVariables}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
              style={{ borderColor: 'var(--glass-border-light)' }}
              placeholder="Enter survey description"
              rows={3}
            />
          </div>

          {/* Confirmation when all variables filled */}
          {hasVariables && allVariablesFilled && (
            <div
              className="mb-4 p-3 rounded-lg text-xs flex items-center gap-2"
              style={{ backgroundColor: 'var(--primary-50)', color: 'var(--color-accent)' }}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              All variables filled. Values will be substituted into question titles, descriptions, and options.
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="glass-btn-secondary glass-btn-sm"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="glass-btn-primary glass-btn-sm"
              disabled={loading || !title.trim() || (hasVariables && !allVariablesFilled)}
            >
              {loading ? 'Creating...' : 'Create Survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFromTemplateModal;
